import IconNameEnum from "constants/IconNameEnum";
import React from "react";
import { Breadcrumb, Col, Row } from "react-bootstrap";
import { connect, DispatchProp } from "react-redux";
import FormatUtility from "utilities/FormatUtility";
import NavigationUtility from "utilities/NavigationUtility";
import { BreadcrumbMapping } from "../../../constants/BreadcrumbMapping";
import IAction from "../../../models/IAction";
import IStore from "../../../models/IStore";
import BreadcrumbLinkModel from "../../../stores/shared/models/BreadcrumbLinkModel";
import BaseDropdown, { BaseDropdownItem } from "../base-content/BaseDropdown";
import BaseButton from "../base-form/BaseButton";
import BaseTruncatedText from "../base-form/BaseTruncatedText";

interface IProps {
    level?: number;
    labels?: { [key: string]: string }[];
    goLevel1?: () => void;
    goLevel2?: () => void;
    goLevel3?: () => void;
}
interface IState {}
interface IStateToProps {
    pathname: string;
}

const mapStateToProps = (state: IStore): IStateToProps => ({
    pathname: state.router.location.pathname,
});

class BaseBreadcrumb extends React.Component<IProps & IStateToProps & DispatchProp<IAction<any>>, IState> {
    private mapLinks = (
        obj: BreadcrumbLinkModel,
        path: BreadcrumbLinkModel[] = [] as BreadcrumbLinkModel[]
    ): BreadcrumbLinkModel[] => {
        const { pathname, level } = this.props;
        // add path to tree
        path = path.concat(
            new BreadcrumbLinkModel({
                ...obj,
                sub: undefined, // remove children
            })
        );
        // check pathname and level
        if (FormatUtility.getPath(obj.path) === FormatUtility.getPath(pathname) && obj.level === (level ?? 0)) {
            // current page found
            return path;
        } else {
            // search below
            if (obj.sub !== undefined) {
                // search inside children
                return (
                    obj.sub
                        .map((child: BreadcrumbLinkModel) => this.mapLinks(child, path))
                        .find((path: BreadcrumbLinkModel[]) => path && path.length > 0) ?? ([] as BreadcrumbLinkModel[])
                );
            } else {
                // not found
                return [] as BreadcrumbLinkModel[];
            }
        }
    };

    private generateLinks = () => {
        return this.mapLinks(BreadcrumbMapping);
    };

    private getLabel = (label: string | undefined): string => {
        if (label) {
            const { labels } = this.props;
            if (labels && labels.find((x) => x[label])) {
                const replaced = labels.find((x) => x[label]);
                if (replaced) {
                    return replaced[label];
                }
            }
            return label;
        }
        return "";
    };

    private redirectToPage = (link: BreadcrumbLinkModel | undefined) => {
        if (link) {
            const { path, level } = link;
            if (path) {
                const { goLevel1, goLevel2, goLevel3, pathname } = this.props;
                let method = undefined;
                switch (level) {
                    case 3:
                        method = goLevel3;
                        break;
                    case 2:
                        method = goLevel2;
                        break;
                    case 1:
                        method = goLevel1;
                        break;
                }
                if (method) {
                    method();
                } else if (level === 3) {
                    NavigationUtility.redirectToPage(pathname);
                } else {
                    NavigationUtility.redirectToPage(path);
                }
            }
        }
    };

    private onTruncatedLinkClick = (crumb: BreadcrumbLinkModel[], code: string) => {
        const link = crumb.find((x) => x.code === code);
        this.redirectToPage(link);
    };

    private getCrumbs = (items: BreadcrumbLinkModel[]) => {
        let crumbs = [] as BreadcrumbLinkModel[][];
        if (items.length > 4) {
            crumbs.push([items[0]]);
            let truncated = items.filter((x, i) => i > 0 && i < items.length - 2);
            crumbs.push(truncated);
            crumbs.push([items[items.length - 2]]);
            crumbs.push([items[items.length - 1]]);
        } else {
            items.map((item) => crumbs.push([item]));
        }
        return crumbs;
    };

    private getLastLink = (items: BreadcrumbLinkModel[]) => {
        if (items.length > 1) {
            const last = items.filter((item, index) => index < items.length - 1 && item.path.length > 0).pop();
            if (last) {
                return last;
            }
        }
        return undefined;
    };

    public render(): JSX.Element {
        const items = this.generateLinks();
        const crumbs = this.getCrumbs(items);
        const last = this.getLastLink(items);

        return (
            <Row className="mb-3">
                <Col>
                    <Breadcrumb className="d-none d-lg-block" listProps={{ className: "mb-0 px-0 bg-white" }}>
                        {crumbs.map((crumb: BreadcrumbLinkModel[], index: number) => {
                            const isTruncated = crumb.length > 1;
                            const link = isTruncated === false ? crumb[0] : undefined;
                            const isLast = crumbs.length === index + 1;
                            const isLink = link !== undefined && link.path.length > 0 && isLast === false;
                            return isTruncated ? (
                                <BaseDropdown
                                    key={index}
                                    items={crumb.map((link) => {
                                        return {
                                            code: link.code,
                                            label: link.label,
                                            className: link.path ? "" : "disabled",
                                        } as BaseDropdownItem;
                                    })}
                                    onClickHandler={(code: string) => this.onTruncatedLinkClick(crumb, code)}
                                    customToggleId={"breadcrumb-dropdown-" + index}
                                    customToggleElement={<span>...</span>}
                                    customToggleLabel="..."
                                    className="breadcrumb-item d-flex"
                                />
                            ) : (
                                <Breadcrumb.Item
                                    key={index}
                                    onClick={isLink ? () => this.redirectToPage(link) : undefined}
                                    className={isLast ? "text-truncate" : ""}
                                    linkProps={{
                                        className: [
                                            "text-ccq-default",
                                            isLink === false ? "text-decoration-none cursor-default" : "",
                                            isLast ? "text-truncate" : "",
                                        ].join(" "),
                                    }}
                                    title={link?.title}
                                >
                                    <BaseTruncatedText text={this.getLabel(link?.label)} />
                                </Breadcrumb.Item>
                            );
                        })}
                    </Breadcrumb>
                    <BaseButton
                        type="button"
                        color="link"
                        label={this.getLabel(last?.label)}
                        icon={IconNameEnum.Indietro}
                        onClickHandler={() => this.redirectToPage(last)}
                        className="d-lg-none p-0 mx-0 mt-2 mb-3 btn-sm text-ccq-default"
                    />
                </Col>
            </Row>
        );
    }
}

export { BaseBreadcrumb as Unconnected };
export default connect(mapStateToProps)(BaseBreadcrumb);
